import { useState, useMemo, useCallback } from "react";
import { combineSections } from "./GlobalSearch.utils";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import SearchIcon from "components/Icons/SearchIcon";
import { useNavigate } from "react-router-dom";
import useGroupsContext from "hooks/useGroupsContext";
import useAthleteStore from "providers/AthletesProvider/useAthleteStore";
import { firstBy } from "thenby";
import * as Components from "./GlobalSearch.components";
import useCanDo from "hooks/useCanDo";
import logEvent from "utils/logEvent";
import AthleteHelpers from "helpers/AthleteHelpers";
import AutocompleteOptionColourable from "components/AutocompleteOptionColourable";
import { useTranslation } from "react-i18next";
import useNaturalSort from "hooks/useNaturalSort";
import MuiThemeProvider from "components/MuiThemeProvider";

function GlobalSearch() {
  const [inputValue, setInputValue] = useState("");
  const athletes = useAthleteStore();
  const [groups] = useGroupsContext();
  const navigate = useNavigate();
  const canDo = useCanDo();
  const { t } = useTranslation();
  const naturalCompare = useNaturalSort();
  const sections = useMemo(
    () =>
      combineSections({
        Athletes: athletes.sort(firstBy("fullName", { cmp: naturalCompare }).thenBy("id")),
        Groups: canDo("group.any") ? groups.sort(firstBy("name", { cmp: naturalCompare })) : [],
      }),
    [athletes, canDo, naturalCompare, groups]
  );

  const handleSelection = useCallback(
    (_, option) => {
      if (option?.url) {
        logEvent("HUB_GLOBAL_SEARCH", { section: option.section });
        navigate(option.url);
      }
    },
    [navigate]
  );

  const handleInputChange = useCallback((_, newInputValue, reason) => {
    setInputValue(reason === "reset" ? "" : newInputValue);
  }, []);

  const inputPlaceholder = useMemo(() => {
    if (canDo("group.any")) {
      return t("global.search_for_athletes_or_groups");
    }
    return t("global.search_for_athletes");
  }, [t, canDo]);

  const renderSearchInput = useCallback(
    (params) => (
      <Components.InputContainerPaper ref={params.InputProps.ref}>
        <Components.PaddedInputBase
          fullWidth
          inputProps={params.inputProps}
          placeholder={inputPlaceholder}
          endAdornment={<SearchIcon />}
        />
      </Components.InputContainerPaper>
    ),
    [inputPlaceholder]
  );

  const getOptionLabel = useCallback(
    (option) => (option.section === "Groups" ? option.name : AthleteHelpers.getFullName(option)),
    []
  );

  const renderOption = useCallback(
    (props, option) => (
      <AutocompleteOptionColourable
        {...props}
        key={option.id}
        colour={option.section === "Groups" ? option.colour : undefined}
        data-sensitive={option.section !== "Groups"}
      >
        {getOptionLabel(option)}
      </AutocompleteOptionColourable>
    ),
    [getOptionLabel]
  );

  return (
    <MuiThemeProvider>
      <Box>
        <Autocomplete
          id="global-search-athletes-groups"
          options={sections}
          autoHighlight
          getOptionLabel={getOptionLabel}
          groupBy={(option) => option.section}
          value={null}
          onChange={handleSelection}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          renderInput={renderSearchInput}
          renderOption={renderOption}
        />
      </Box>
    </MuiThemeProvider>
  );
}

export default GlobalSearch;
